/**
 * Blog Statistics Utilities
 *
 * Calculate various statistics from blog posts arrays.
 * Used by the BlogManagement page for dashboard metrics.
 */

import type { BlogPost, BlogStats, CategoryStats, TagStats, PublishingCalendar } from '../types';

/**
 * Calculate basic blog post statistics
 * @param posts - Array of blog posts
 * @returns Stats object with counts by status
 */
export const calculateBlogStats = (posts: BlogPost[]): BlogStats => {
  return {
    total: posts.length,
    published: posts.filter(p => p.status === 'published').length,
    draft: posts.filter(p => p.status === 'draft').length,
    archived: posts.filter(p => p.status === 'archived').length,
    scheduled: posts.filter(p => p.status === 'scheduled').length,
  };
};

/**
 * Calculate category distribution statistics
 * @param posts - Array of blog posts
 * @returns Array of category stats sorted by count descending
 */
export const calculateCategoryStats = (posts: BlogPost[]): CategoryStats[] => {
  const counts: Record<string, number> = {};

  posts.forEach(post => {
    if (post.category) {
      counts[post.category] = (counts[post.category] ?? 0) + 1;
    }
  });

  return Object.entries(counts)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
};

/**
 * Calculate tag frequency statistics
 * @param posts - Array of blog posts
 * @param limit - Maximum number of tags to return (default: 12)
 * @returns Array of tag stats sorted by count descending
 */
export const calculateTagStats = (posts: BlogPost[], limit = 12): TagStats[] => {
  const counts: Record<string, number> = {};

  posts.forEach(post => {
    post.tags?.forEach(tag => {
      const normalized = tag.trim();
      if (!normalized) return;
      counts[normalized] = (counts[normalized] ?? 0) + 1;
    });
  });

  return Object.entries(counts)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);
};

/**
 * Calculate publishing calendar data
 * @param posts - Array of blog posts
 * @returns Publishing calendar statistics
 */
export const calculatePublishingCalendar = (posts: BlogPost[]): PublishingCalendar => {
  const now = new Date();

  // Calculate week range
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - now.getDay());
  startOfWeek.setHours(0, 0, 0, 0);
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 7);
  endOfWeek.setHours(23, 59, 59, 999);

  // Calculate month range
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  endOfMonth.setHours(23, 59, 59, 999);

  // Helper to check if date is within range
  const isWithinRange = (isoDate: string | null | undefined, start: Date, end: Date) => {
    if (!isoDate) return false;
    const candidate = new Date(isoDate);
    return candidate.getTime() >= start.getTime() && candidate.getTime() <= end.getTime();
  };

  // Get scheduled posts
  const scheduledPosts = posts.filter(post => post.status === 'scheduled');

  // Find next scheduled post
  const nextScheduledPost = scheduledPosts
    .filter(post => post.scheduled_for)
    .sort((a, b) => new Date(a.scheduled_for!).getTime() - new Date(b.scheduled_for!).getTime())[0];

  return {
    scheduledCount: scheduledPosts.length,
    nextScheduledPost: nextScheduledPost ? {
      id: nextScheduledPost.id,
      title: nextScheduledPost.title,
      scheduled_for: nextScheduledPost.scheduled_for!,
    } : undefined,
    publishedThisWeek: posts.filter(post => isWithinRange(post.published_at, startOfWeek, endOfWeek)).length,
    publishedThisMonth: posts.filter(post => isWithinRange(post.published_at, startOfMonth, endOfMonth)).length,
  };
};
